import { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { api } from '../../utils/api';
import { 
  CreditCard, 
  Edit2, 
  CheckCircle2, 
  Plus, 
  Trash2, 
  DollarSign, 
  Layers,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

export const AdminPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    period: 'monthly',
    features: [],
    recommended: false,
    order: 0
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await api.plans.getAll();
      setPlans(res.data || []);
    } catch (error) {
      toast.error('Error al cargar planes');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (plan = null) => {
    if (plan) {
      setCurrentPlan(plan);
      setFormData({
        ...plan,
        features: plan.features || []
      });
    } else {
      setCurrentPlan(null);
      setFormData({
        name: '',
        price: '',
        period: 'monthly',
        features: [],
        recommended: false,
        order: plans.length + 1
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentPlan) {
        await api.plans.update(currentPlan.id, formData);
        toast.success('Protocolo de precios actualizado');
      } else {
        // En tu api.js actual no tienes planes.create, pero lo implemento aquí asumiendo la estructura
        await api.plans.update(0, formData); // Placeholder o podrías añadir create a api.js
        toast.success('Nuevo plan configurado');
      }
      setIsModalOpen(false);
      fetchPlans();
    } catch (error) {
      toast.error('Error al configurar plan');
    }
  };

  const toggleFeature = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  return (
    <AdminLayout>
      <div className="space-y-12">
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <span className="text-[#C6FF00] font-black uppercase tracking-[0.3em] text-[10px] block mb-2">Monetization Engine</span>
            <h1 className="text-4xl font-black tracking-tighter uppercase italic">Modelos de Suscripción</h1>
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-10 rounded-[3rem] bg-[#030303] border ${plan.recommended ? 'border-[#C6FF00]/40 shadow-[0_0_50px_rgba(198,255,0,0.1)]' : 'border-white/5'} transition-all relative overflow-hidden group`}
            >
              {plan.recommended && (
                <div className="absolute top-0 right-0 px-6 py-2 bg-[#C6FF00] text-black text-[8px] font-black uppercase tracking-[0.3em] rounded-bl-2xl">
                  RECOMENDADO
                </div>
              )}

              <div className="mb-10 flex items-center justify-between">
                <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-white/50 group-hover:text-[#C6FF00] transition-colors">
                  <CreditCard size={24} />
                </div>
                <button 
                  onClick={() => handleOpenModal(plan)}
                  className="p-4 bg-white/5 rounded-2xl text-white/20 hover:text-white transition-all"
                >
                  <Edit2 size={16} />
                </button>
              </div>

              <div className="space-y-2 mb-10">
                <h3 className="text-2xl font-black uppercase tracking-tight text-white">{plan.name}</h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-black text-white tracking-tighter italic">${plan.price}</span>
                  <span className="text-white/20 text-[10px] font-black uppercase tracking-widest">/ {plan.period === 'monthly' ? 'MES' : 'AÑO'}</span>
                </div>
              </div>

              <div className="space-y-4 mb-12">
                {plan.features?.map((feature, i) => (
                  <div key={i} className="flex items-center gap-4 text-white/40 group-hover:text-white/60 transition-colors">
                    <CheckCircle2 size={14} className="text-[#C6FF00] shrink-0" />
                    <span className="text-xs font-black uppercase tracking-widest">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="pt-8 border-t border-white/5 flex items-center justify-between">
                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-white/10 italic">Nivel {plan.order}</span>
                <div className="flex items-center gap-2 text-[#C6FF00]/40">
                  <span className="text-[8px] font-black tracking-widest uppercase italic">Estructura Activa</span>
                  <div className="w-1.5 h-1.5 rounded-full bg-[#C6FF00] animate-pulse"></div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Modal Plan Rebuild */}
        {isModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              className="absolute inset-0 bg-black/95 backdrop-blur-xl" 
              onClick={() => setIsModalOpen(false)} 
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative w-full max-w-2xl bg-[#0a0a0a] border border-white/5 rounded-[3rem] p-12 overflow-hidden"
            >
              <form onSubmit={handleSubmit} className="space-y-10">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-3xl font-black uppercase italic tracking-tighter">Modificar Protocolo</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <label className="text-[10px] font-black uppercase tracking-widest text-white/40">Nombre del Plan</label>
                    <input
                      required
                      value={formData.name}
                      onChange={e => setFormData({...formData, name: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white outline-none focus:border-[#C6FF00]/40"
                    />
                  </div>
                  <div className="space-y-4">
                    <label className="text-[10px] font-black uppercase tracking-widest text-white/40">Precio Unitario (USD)</label>
                    <input
                      required
                      type="number"
                      value={formData.price}
                      onChange={e => setFormData({...formData, price: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white outline-none focus:border-[#C6FF00]/40"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <label className="text-[10px] font-black uppercase tracking-widest text-white/40 italic mb-4 block">Características del Nivel</label>
                  <div className="grid grid-cols-1 gap-4">
                    {['Soporte 24/7', 'Acceso Prioritario', 'Capacidad Ilimitada', 'Consultoría Estratégica', 'API Integración'].map(f => (
                      <button
                        key={f}
                        type="button"
                        onClick={() => toggleFeature(f)}
                        className={`p-5 rounded-2xl border transition-all flex items-center justify-between text-[10px] font-black uppercase tracking-widest ${
                          formData.features.includes(f) ? 'bg-[#C6FF00]/10 border-[#C6FF00]/40 text-[#C6FF00]' : 'bg-white/5 border-white/5 text-white/40'
                        }`}
                      >
                        {f}
                        {formData.features.includes(f) ? <CheckCircle2 size={16} /> : <div className="w-4 h-4 rounded-full border border-white/10"></div>}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="pt-6 border-t border-white/5 flex flex-col gap-4">
                   <button
                    type="submit"
                    className="w-full py-5 bg-[#C6FF00] text-black rounded-2xl font-black uppercase tracking-widest text-[10px] hover:scale-105 transition-all"
                  >
                    Actualizar Estructura de Precios
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="w-full py-5 bg-white/5 text-white/20 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:text-white transition-all"
                  >
                    Cerrar
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
